import React from 'react'
import {
    AlertProps,
    Box,
    Button,
    ButtonProps,
    SxProps,
    useMediaQuery,
    useTheme
} from '@mui/material'
import {
    CoreAlert,
    CoreTypography,
    LeafIcon
} from '@thriveglobal/thrive-web-leafkit'
import { useCrossAppNavigation } from '@thriveglobal/thrive-web-core'
import type { IconName } from '@fortawesome/fontawesome-common-types'
import type { InAppMessageDto } from '../../../../../graphql/generated/autogenerated'

interface ActionBanner {
    uuid: string
    callToActionText: string
    callToActionUrl: string
    header?: string
    body: string
    page: string
    iconName?: IconName
    dismissible?: boolean
    backgroundColorPalette?: string
    image?: {
        alternativeText: string
        url: string
    }
}

interface BannerProps {
    banner: ActionBanner | InAppMessageDto
    onClose?: () => void
    alertProps?: AlertProps
    buttonProps?: ButtonProps
    imageSx?: SxProps
}

export const ActionBanner: React.FC<BannerProps> = ({
    banner,
    onClose,
    alertProps,
    buttonProps,
    imageSx
}) => {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const navigate = useCrossAppNavigation()

    const { sx: buttonSx, ...otherButtonProps } = buttonProps || {}
    const { sx: alertSx, ...otherAlertProps } = alertProps || {}
    return (
        <CoreAlert
            onClose={banner?.dismissible ? onClose : undefined}
            elevation={8}
            icon={
                banner?.iconName ? (
                    <LeafIcon icon={banner?.iconName as IconName} />
                ) : (
                    false
                )
            }
            variant="standard"
            severity="info"
            sx={{
                my: 2,
                '& .MuiAlert-action': {
                    pt: isMobile ? 0 : 1.25
                },
                '& .MuiAlert-icon': {
                    fontSize: 16,
                    display: 'flex',
                    alignItems: 'center'
                },
                '&&.MuiAlert-standardInfo, &&.MuiAlert-filledInfo': {
                    bgcolor: banner.backgroundColorPalette ?? 'background.paper'
                },
                ...(alertSx ?? {})
            }}
            {...otherAlertProps}
        >
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                flexDirection={isMobile ? 'column' : 'row'}
                gap={isMobile ? 2 : 0}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3,
                        height: '100%',
                        flexDirection: isMobile ? 'column' : 'row'
                    }}
                >
                    {banner?.image?.url && (
                        <Box
                            sx={{
                                width: 120,
                                height: 30,
                                maxHeight: 30,
                                ...(imageSx ?? {})
                            }}
                        >
                            <img
                                src={banner?.image?.url ?? ''}
                                alt={banner?.image?.alternativeText ?? ''}
                                width="100%"
                                height="100%"
                            />
                        </Box>
                    )}
                    <Box>
                        {banner?.header && (
                            <CoreTypography variant="overline">
                                {banner.header}
                            </CoreTypography>
                        )}
                        <CoreTypography
                            variant="body1"
                            sx={{ color: 'text.primary' }}
                        >
                            {banner.body}
                        </CoreTypography>
                    </Box>
                </Box>

                <Button
                    sx={{
                        backgroundColor: 'inherit',
                        ...(buttonSx ?? {})
                    }}
                    variant="outlined"
                    size="large"
                    onClick={() => {
                        navigate(banner.callToActionUrl ?? '')
                    }}
                    {...otherButtonProps}
                >
                    <CoreTypography customVariant="buttonLarge">
                        {banner.callToActionText}
                    </CoreTypography>
                </Button>
            </Box>
        </CoreAlert>
    )
}

export default ActionBanner
